<template>
    <div class="dashboard-container">
        <Sidebar />

        <main class="main-content">
            <header class="header-dashboard">
                <div>
                    <h1>{{ $t('admin.title') }}</h1>
                    <p>{{ $t('admin.subtitle') }}</p>
                </div>
                <div class="user-profile">
                    <div class="avatar-circle">{{ userInitials }}</div>
                    <div>
                        <p class="user-name">{{ userEmail }}</p>
                        <p class="user-role">{{ $t('admin.admin_role') }}</p>
                    </div>
                </div>
            </header>

            <!-- Table with actions -->
            <section class="table-container">
                <div class="table-header">
                    <h3>{{ $t('admin.users_list') }}</h3>
                    <div class="header-actions">
                        <button class="btn-filter" @click="abrirModalNuevoUsuario">
                            <ion-icon name="add-outline"></ion-icon> {{ $t('admin.new_user') }}
                        </button>
                        <button class="btn-export" @click="exportarExcel">
                            <ion-icon name="document-text-outline"></ion-icon> {{ $t('admin.export') }}
                        </button>
                    </div>
                </div>
                
                <div v-if="loading" class="loading-state">
                    <p>{{ $t('admin.loading') }}</p>
                </div>
                
                <div v-else-if="usuarios.length === 0" class="empty-state">
                    <p>{{ $t('admin.no_users') }}</p>
                </div>
                
                <table v-else class="polizas-table">
                    <thead>
                        <tr>
                            <th>{{ $t('admin.id') }}</th>
                            <th>{{ $t('admin.user_email') }}</th>
                            <th>{{ $t('admin.role') }}</th>
                            <th>{{ $t('admin.created_at') }}</th>
                            <th>{{ $t('admin.last_login') }}</th>
                            <th>{{ $t('admin.status') }}</th>
                            <th>{{ $t('admin.actions') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="usuario in usuarios" :key="usuario.id">
                            <td>{{ usuario.id }}</td>
                            <td>{{ usuario.user }}</td>
                            <td>
                                <span :class="['role-badge', usuario.rol === 'admin' ? 'admin' : 'user']">
                                    {{ usuario.rol === 'admin' ? $t('admin.admin') : $t('admin.client') }}
                                </span>
                            </td>
                            <td>{{ formatDate(usuario.created_at) }}</td>
                            <td>{{ formatDate(usuario.last_login) || '-' }}</td>
                            <td>
                                <span :class="['status-badge', usuario.activo ? 'activo' : 'inactivo']">
                                    {{ usuario.activo ? $t('admin.active') : $t('admin.inactive') }}
                                </span>
                            </td>
                            <td class="actions">
                                <button @click="editarUsuario(usuario)" class="btn-icon" :title="$t('admin.edit')">
                                    <ion-icon name="create-outline"></ion-icon>
                                </button>
                                <button @click="eliminarUsuario(usuario)" class="btn-icon danger" :title="$t('admin.delete')">
                                    <ion-icon name="trash-outline"></ion-icon>
                                </button>
                                <button @click="resetearPassword(usuario)" class="btn-icon" :title="$t('admin.reset_password')">
                                    <ion-icon name="key-outline"></ion-icon>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </section>
        </main>

        <!-- Modal Crear/Editar Usuario -->
        <div v-if="mostrarModalUsuario" class="modal-overlay" @click.self="cerrarModalUsuario">
            <div class="modal-content">
                <div class="modal-header">
                    <h3>{{ modoEdicion ? $t('admin.edit_user') : $t('admin.new_user') }}</h3>
                    <button class="modal-close" @click="cerrarModalUsuario">×</button>
                </div>
                <div class="modal-body">
                    <div class="filter-group">
                        <label>{{ $t('admin.email_user') }} <span class="required">*</span></label>
                        <input type="email" v-model="usuarioForm.user" :placeholder="$t('admin.email_placeholder')">
                    </div>
                    <div class="filter-group" v-if="!modoEdicion">
                        <label>{{ $t('admin.password') }} <span class="required">*</span></label>
                        <input type="password" v-model="usuarioForm.pass" :placeholder="$t('admin.password_placeholder')">
                    </div>
                    <div class="filter-group">
                        <label>{{ $t('admin.role_label') }}</label>
                        <select v-model="usuarioForm.rol">
                            <option value="cliente">{{ $t('admin.client') }}</option>
                            <option value="admin">{{ $t('admin.admin') }}</option>
                        </select>
                    </div>
                    <div class="filter-group">
                        <label>{{ $t('admin.status_label') }}</label>
                        <select v-model="usuarioForm.activo">
                            <option :value="1">{{ $t('admin.active') }}</option>
                            <option :value="0">{{ $t('admin.inactive') }}</option>
                        </select>
                    </div>
                </div>
                <div class="modal-footer">
                    <button class="btn-clear" @click="cerrarModalUsuario">{{ $t('admin.cancel') }}</button>
                    <button class="btn-apply" @click="guardarUsuario">{{ $t('admin.save') }}</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { API_URL } from '@/config'
import * as XLSX from 'xlsx'
import Sidebar from '@/components/layout/Sidebar.vue';

export default {
    name: 'AdminUsuarios',
    components: { Sidebar },
    data() {
        return {
            loading: true,
            esAdmin: false,
            userEmail: '',
            userHash: '',
            usuarios: [],
            mostrarModalUsuario: false,
            modoEdicion: false,
            usuarioForm: {
                id: null,
                user: '',
                pass: '',
                rol: 'cliente',
                activo: 1
            }
        }
    },
    computed: {
        userInitials() {
            return this.userEmail ? this.userEmail.substring(0, 2).toUpperCase() : 'AD'
        },
        currentLang() {
            return this.$route.params.lang || 'es'
        }
    },
    mounted() {
        const alreadyReloaded = sessionStorage.getItem('area_privada_reloaded')
        
        if (!alreadyReloaded) {
            sessionStorage.setItem('area_privada_reloaded', 'true')
            setTimeout(() => {
                window.location.reload()
            }, 100)
        } else {
            this.verificarSesion()
        }
    },
    methods: {
        /*irAAreaPrivada() {
            this.$router.push({ name: 'AreaPrivada' })
            setTimeout(() => {
                window.location.reload()
            }, 100)
        },*/
        verificarSesion() {
            const hash = localStorage.getItem('expat_visa_session')
            const user = localStorage.getItem('user')
            
            if (!hash || !user) {
                this.$router.push({ name: 'login' })
                return
            }
            
            this.userEmail = user
            this.userHash = hash
            this.esAdmin = true
            this.cargarUsuarios()
        },
        
        async cargarUsuarios() {
            this.loading = true
            try {
                const response = await fetch(`${API_URL}/com.api.get-admins.php`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer GZ6esKqrszhUK9elWctlHk6kuDcILXu6om4UwOkecWAlzdzIHMVh4LjgSj2KCotA'
                    },
                    body: JSON.stringify({ hash_admin: this.userHash })
                })
                const data = await response.json()
                if (data.success) {
                    this.usuarios = data.usuarios
                }
            } catch (error) {
                console.error('Error:', error)
            } finally {
                this.loading = false
            }
        },
        
        abrirModalNuevoUsuario() {
            this.modoEdicion = false
            this.usuarioForm = {
                id: null,
                user: '',
                pass: '',
                rol: 'cliente',
                activo: 1
            }
            this.mostrarModalUsuario = true
        },
        
        editarUsuario(usuario) {
            this.modoEdicion = true
            this.usuarioForm = {
                id: usuario.id,
                user: usuario.user,
                pass: '',
                rol: usuario.rol,
                activo: usuario.activo
            }
            this.mostrarModalUsuario = true
        },
        
        async guardarUsuario() {
            if (!this.usuarioForm.user) {
                alert(this.$t('admin.email_required'))
                return
            }
            
            if (!this.modoEdicion && !this.usuarioForm.pass) {
                alert(this.$t('admin.password_required'))
                return
            }
            
            try {
                const response = await fetch(`${API_URL}/com.api.save-admin.php`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer GZ6esKqrszhUK9elWctlHk6kuDcILXu6om4UwOkecWAlzdzIHMVh4LjgSj2KCotA'
                    },
                    body: JSON.stringify({
                        ...this.usuarioForm,
                        hash_admin: this.userHash
                    })
                })
                const data = await response.json()
                if (data.success) {
                    this.cargarUsuarios()
                    this.cerrarModalUsuario()
                    alert(this.$t('admin.user_saved'))
                } else {
                    alert(data.error || this.$t('admin.save_error'))
                }
            } catch (error) {
                console.error('Error:', error)
                alert(this.$t('admin.save_error'))
            }
        },
        
        async eliminarUsuario(usuario) {
            if (!confirm(this.$t('admin.confirm_delete', { user: usuario.user }))) return
            
            try {
                const response = await fetch(`${API_URL}/com.api.delete-admin.php`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer GZ6esKqrszhUK9elWctlHk6kuDcILXu6om4UwOkecWAlzdzIHMVh4LjgSj2KCotA'
                    },
                    body: JSON.stringify({
                        id: usuario.id,
                        hash_admin: this.userHash
                    })
                })
                const data = await response.json()
                if (data.success) {
                    this.cargarUsuarios()
                } else {
                    alert(data.error || this.$t('admin.delete_error'))
                }
            } catch (error) {
                console.error('Error:', error)
                alert(this.$t('admin.delete_error'))
            }
        },
        
        async resetearPassword(usuario) {
            const nuevaPass = prompt(this.$t('admin.new_password_prompt', { user: usuario.user }))
            if (!nuevaPass) return
            
            try {
                const response = await fetch(`${API_URL}/com.api.reset-password.php`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer GZ6esKqrszhUK9elWctlHk6kuDcILXu6om4UwOkecWAlzdzIHMVh4LjgSj2KCotA'
                    },
                    body: JSON.stringify({
                        id: usuario.id,
                        password: nuevaPass,
                        hash_admin: this.userHash
                    })
                })
                const data = await response.json()
                if (data.success) {
                    alert(this.$t('admin.password_updated'))
                } else {
                    alert(data.error || this.$t('admin.reset_error'))
                }
            } catch (error) {
                console.error('Error:', error)
                alert(this.$t('admin.reset_error'))
            }
        },
        
        exportarExcel() {
            const datos = this.usuarios.map(u => ({
                ID: u.id,
                Usuario: u.user,
                Rol: u.rol === 'admin' ? this.$t('admin.admin') : this.$t('admin.client'),
                Estado: u.activo ? this.$t('admin.active') : this.$t('admin.inactive'),
                [this.$t('admin.created_at')]: this.formatDate(u.created_at),
                [this.$t('admin.last_login')]: this.formatDate(u.last_login) || '-'
            }))
            const ws = XLSX.utils.json_to_sheet(datos)
            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, ws, this.$t('admin.users_sheet'))
            XLSX.writeFile(wb, `${this.$t('admin.users_filename')}_${new Date().toISOString().split('T')[0]}.xlsx`)
        },
        
        formatDate(dateString) {
            if (!dateString) return '-'
            const date = new Date(dateString)
            return date.toLocaleDateString(this.currentLang === 'en' ? 'en-US' : 'es-ES')
        },
        
        cerrarModalUsuario() {
            this.mostrarModalUsuario = false
        },
        
        cerrarSesion() {
            localStorage.removeItem('expat_visa_session')
            localStorage.removeItem('user')
            localStorage.removeItem('user_name')
            localStorage.removeItem('user_rol')
            this.$router.push({ name: 'login' })
            setTimeout(() => {
                window.location.reload()
            }, 100)
        }
    }
}
</script>

<style scoped>
/* ... mismos estilos que antes ... */
.dashboard-container {
    display: flex;
    min-height: 100vh;
    background: #f5f7fa;
    font-family: 'Open Sans', sans-serif;
}

/* ========== MAIN CONTENT ========== */
.main-content {
    margin-left: 260px;
    flex-grow: 1;
    padding: 40px;
}

/* ========== HEADER ========== */
.header-dashboard {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
}

.header-dashboard h1 {
    font-size: 1.8rem;
    color: #202124;
    margin-bottom: 5px;
}

.header-dashboard p {
    color: #5f6368;
}

.user-profile {
    display: flex;
    align-items: center;
    gap: 12px;
}

.avatar-circle {
    width: 40px;
    height: 40px;
    background: #1C4A8D;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    text-transform: uppercase;
}

.user-name {
    font-weight: 600;
    font-size: 0.9rem;
    margin: 0;
}

.user-role {
    font-size: 0.75rem;
    color: #5f6368;
    margin: 0;
}

/* ========== TABLE ========== */
.table-container {
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    overflow-x: auto;
}

.table-header {
    padding: 20px 25px;
    border-bottom: 1px solid #f1f3f4;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.table-header h3 {
    font-size: 1.1rem;
    color: #202124;
    margin: 0;
}

.polizas-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 600px;
}

.polizas-table th {
    text-align: left;
    background: #f8f9fa;
    padding: 15px 25px;
    color: #5f6368;
    font-weight: 600;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.polizas-table td {
    padding: 18px 25px;
    border-bottom: 1px solid #f1f3f4;
    color: #3c4043;
    font-size: 0.95rem;
}

.status-badge {
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    display: inline-block;
}

.status-badge.emitida {
    background: #e6f4ea;
    color: #1e8e3e;
}

.status-badge.revision {
    background: #fff4e5;
    color: #ef6c00;
}

.btn-table {
    padding: 6px 12px;
    border: 1px solid #dadce0;
    border-radius: 6px;
    background: white;
    color: #3c4043;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s;
    text-decoration: none;
}

.btn-table:hover {
    background: #f8f9fa;
    border-color: #bdc1c6;
}

/* ========== STATES ========== */
.loading-state,
.empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #5f6368;
}

/* ========== RESPONSIVE ========== */
@media (max-width: 768px) {
    .main-content {
        margin-left: 80px;
        padding: 20px;
    }
}

/* ========== BOTÓN FILTRAR ========== */
.btn-filter {
    padding: 8px 16px;
    background: white;
    border: 1px solid #dadce0;
    border-radius: 8px;
    color: #5f6368;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-filter:hover {
    background: #f8f9fa;
    border-color: #bdc1c6;
}

/* ========== EXPORT ========== */
.header-actions {
    display: flex;
    gap: 10px;
}

.btn-export {
    padding: 8px 16px;
    background: #28a745;
    color: white;
    border: none;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.85rem;
    cursor: pointer;
}

.btn-export:hover {
    background: #1e7e34;
}

/* ========== MODAL ========== */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1100;
}

.modal-content {
    background: white;
    border-radius: 16px;
    width: 90%;
    max-width: 500px;
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 25px;
    border-bottom: 1px solid #e1e4e8;
    background: white;
    flex-shrink: 0;
}

.modal-header h3 {
    margin: 0;
    color: #202124;
}

.modal-close {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #5f6368;
}

.modal-body {
    padding: 25px;
    overflow-y: auto;
    flex-grow: 1;
}

.modal-footer {
    padding: 20px 25px;
    border-top: 1px solid #e1e4e8;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    background: white;
    flex-shrink: 0;
}

.filter-group {
    margin-bottom: 20px;
}

.filter-group label {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    font-size: 0.9rem;
    color: #3c4043;
}

.filter-group select,
.filter-group input {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #dadce0;
    border-radius: 8px;
    font-size: 0.9rem;
}

.filter-group select:focus,
.filter-group input:focus {
    outline: none;
    border-color: #1C4A8D;
}

.btn-clear {
    padding: 10px 20px;
    background: white;
    border: 1px solid #dadce0;
    border-radius: 8px;
    cursor: pointer;
}

.btn-apply {
    padding: 10px 20px;
    background: #1C4A8D;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
}

.btn-apply:hover {
    background: #153a70;
}

.divider {
    height: 1px;
    background: #e1e4e8;
    margin: 15px 0;
}

.actions {
    display: flex;
    gap: 8px;
}

.btn-icon {
    background: none;
    border: none;
    padding: 6px;
    cursor: pointer;
    color: #5f6368;
    border-radius: 4px;
}

.btn-icon:hover {
    background: #f1f3f4;
}

.btn-icon.danger:hover {
    background: #fce8e6;
    color: #d93025;
}

.role-badge {
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
}

.role-badge.admin {
    background: #e8f0fe;
    color: #1C4A8D;
}

.role-badge.user {
    background: #e6f4ea;
    color: #1e8e3e;
}

.status-badge.activo {
    background: #e6f4ea;
    color: #1e8e3e;
}

.status-badge.inactivo {
    background: #fce8e6;
    color: #d93025;
}

.required {
    color: #d93025;
}
</style>